import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
/**
 * Overview Dashboard Module - Updated to use Real API Data
 * 
 * This dashboard now connects to the actual Firebase Functions API endpoint
 * that calculates metrics from real Firebase collections:
 * 
 * REAL DATA SOURCES (via API):
 * - Client Reports (/organizations/{orgId}/reports): Weekly health questionnaires
 * - Exercise Data (exerciseData in reports): Workout completion tracking  
 * - Health Data (healthData in reports): Daily weight, sleep, mood, energy, stress (1-10 scales)
 * - Message History (/organizations/{orgId}/messageHistory): Coach-client communications
 * - Progress Photos (/organizations/{orgId}/progressPhotos): Photo uploads with timestamps
 * - Client Info (/organizations/{orgId}/clients): Demographics and status
 */
import {
  Box,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  useTheme,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent,
  LinearProgress,
  Paper,
} from "@mui/material";
import {
  People as PeopleIcon,
  FitnessCenter as WorkoutIcon,
  Schedule as TimeIcon,
  TrendingUp as GrowthIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Message as MessageIcon,
  Psychology as AIIcon,
  Refresh as RefreshIcon,
  VideoLibrary as VideoLibraryIcon,
  Chat as ChatIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  HourglassEmpty as WaitingIcon,
  Rocket as RocketIcon,
  Star as StarIcon,
  Timeline as TimelineIcon,
} from "@mui/icons-material";
import MetricCard from "../shared/MetricCard";
import ChartContainer from "../shared/ChartContainer";
import DataCollectionBanner from "../shared/DataCollectionBanner";
import { SimpleBarChart, SimpleLineChart } from "../shared/SimpleCharts";
import { consola } from "consola";
import {
  useDashboardOverview,
  useAIInsights,
} from "../../../../../hooks/useDashboardOverview";
import useEntitlements from "../../../../../hooks/useEntitlements";
import UpgradePrompt from "../../../../../components/organisms/UpgradePrompt";

const OverviewModule = ({ organizationId }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { hasFeature, organizationData } = useEntitlements();
  const canUseAiInsights = hasFeature("ai_insights");

  // Map icon string names to actual icon components
  const getIconComponent = (iconName) => {
    const iconMap = {
      Message: MessageIcon,
      Check: CheckIcon,
      Schedule: TimeIcon,
      Warning: WarningIcon,
      People: PeopleIcon,
      FitnessCenter: WorkoutIcon,
      TrendingUp: GrowthIcon,
      Psychology: AIIcon,
      Refresh: RefreshIcon,
      VideoLibrary: VideoLibraryIcon,
      Chat: ChatIcon,
    };
    return iconMap[iconName] || MessageIcon; // Default to MessageIcon if not found
  };
  // Configuration state
  const [config, setConfig] = useState({
    reportQualityThreshold: 40,
    clientComplianceThreshold: 60,
    useAverageCompliance: true,
    workoutComplianceThreshold: 60,
    photoComplianceThreshold: 60,
    enableDetailedLogging: false,
  });

  const [showSettings, setShowSettings] = useState(false);

  // Date range for AI insights
  const dateRange = useMemo(
    () => ({
      start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
      end: new Date(),
    }),
    [],
  );

  // React Query hooks for data fetching
  const {
    data: overviewData,
    isLoading: loading,
    error,
    forceRefresh,
    isRefetching: refreshing,
    dataUpdatedAt,
  } = useDashboardOverview(organizationId, config);

  const {
    data: aiInsightsData,
    isLoading: aiInsightsLoading,
    error: aiInsightsError,
  } = useAIInsights(canUseAiInsights ? organizationId : null, dateRange);

  // Combine overview data with AI insights
  const data = useMemo(() => {
    if (config.enableDetailedLogging) {
      consola.debug("Overview module data", {
        overviewData,
        aiInsightsData,
        loading,
        aiInsightsLoading,
      });
    }

    // Provide fallback empty structures when data is still loading
    const combinedData = {
      kpis: overviewData?.kpis || {},
      activityData: overviewData?.activityData || [],
      growthData: overviewData?.growthData || [],
      alerts: overviewData?.alerts || [],
      quickActions: overviewData?.quickActions || [],
      aiInsights: aiInsightsData?.insights || [],
    };

    return combinedData;
  }, [
    overviewData,
    aiInsightsData,
    loading,
    aiInsightsLoading,
    config.enableDetailedLogging,
  ]);

  // Check if this is a new organization that needs data collection guidance
  const needsDataCollection = useMemo(() => {
    if (loading || !overviewData) return false;

    // Check if we have minimal data
    const activeClients = data.kpis.activeClients?.value || 0;
    const totalReports = data.kpis.weeklyReportCompliance?.value || 0;
    const hasActivity = data.activityData.length > 0;

    // Show banner if we have few clients or minimal activity
    return activeClients < 3 || totalReports < 20 || !hasActivity;
  }, [data, loading, overviewData]);

  // Format last updated time
  const lastRefresh = useMemo(() => {
    return dataUpdatedAt ? new Date(dataUpdatedAt) : null;
  }, [dataUpdatedAt]);
  // Handle refresh - now uses React Query
  const handleRefresh = async () => {
    if (!organizationId || loading || refreshing) return;

    try {
      if (config.enableDetailedLogging) {
        consola.debug("Refreshing overview data");
      }
      await forceRefresh();
    } catch (err) {
      console.error("Error refreshing data:", err);
    }
  };

  const handleConfigChange = (key, value) => {
    setConfig((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleApplyConfig = () => {
    setShowSettings(false);
    // Config change will automatically trigger React Query refetch due to queryKey dependency
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "warning":
        return <WarningIcon sx={{ color: theme.palette.warning.main }} />;
      case "success":
        return <CheckIcon sx={{ color: theme.palette.success.main }} />;
      default:
        return <MessageIcon sx={{ color: theme.palette.info.main }} />;
    }
  };

  const getAlertColor = (type) => {
    switch (type) {
      case "warning":
        return "warning";
      case "success":
        return "success";
      default:
        return "info";
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Welcome Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
          Welcome back! 👋
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Here&apos;s what&apos;s happening with your coaching business today
        </Typography>
        {lastRefresh && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Typography>
        )}
        {(refreshing || loading) && (
          <Box sx={{ mt: 1, display: "flex", gap: 1, alignItems: "center" }}>
            {refreshing && (
              <Chip label="Refreshing..." size="small" color="primary" />
            )}
            {loading && <Chip label="Loading..." size="small" color="info" />}
          </Box>
        )}
      </Box>

      {/* Error Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          Dashboard Error: {error?.message || "Failed to load dashboard data"}
        </Alert>
      )}
      {canUseAiInsights && aiInsightsError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          AI Insights Error:{" "}
          {aiInsightsError?.message || "Failed to load AI insights"} (Dashboard
          data still available)
        </Alert>
      )}

      {/* Data Collection Banner for New Organizations */}
      {needsDataCollection && !loading && (
        <DataCollectionBanner
          activeClients={data.kpis.activeClients?.value || 0}
          primaryActionTarget="/home/clients"
        />
      )}

      {/* Loading State - Show loading cards */}
      {loading && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {[1, 2, 3, 4, 5].map((index) => (
            <Grid key={index} item xs={12} sm={6} md={2.4}>
              <MetricCard title="Loading..." value={0} loading={true} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dashboard Content - Always show unless loading */}
      {!loading && (
        <>
          {/* KPI Cards */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Active Clients"
                value={data.kpis.activeClients?.value}
                change={data.kpis.activeClients?.change}
                trend={data.kpis.activeClients?.trend}
                subtitle={data.kpis.activeClients?.subtitle}
                description={data.kpis.activeClients?.description}
                icon={<PeopleIcon />}
                variant="default"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Workout Completion"
                value={data.kpis.workoutCompletion?.value}
                change={data.kpis.workoutCompletion?.change}
                trend={data.kpis.workoutCompletion?.trend}
                subtitle={data.kpis.workoutCompletion?.subtitle}
                description={data.kpis.workoutCompletion?.description}
                format="percentage"
                icon={<WorkoutIcon />}
                variant="success"
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Progress Photo Compliance"
                value={data.kpis.progressPhotoCompliance?.value}
                change={data.kpis.progressPhotoCompliance?.change}
                trend={data.kpis.progressPhotoCompliance?.trend}
                subtitle={data.kpis.progressPhotoCompliance?.subtitle}
                description={data.kpis.progressPhotoCompliance?.description}
                format="percentage"
                icon={<CheckIcon />}
                variant={
                  data.kpis.progressPhotoCompliance?.value >= 80
                    ? "success"
                    : "warning"
                }
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Weekly Report Compliance"
                value={data.kpis.weeklyReportCompliance?.value}
                change={data.kpis.weeklyReportCompliance?.change}
                trend={data.kpis.weeklyReportCompliance?.trend}
                subtitle={data.kpis.weeklyReportCompliance?.subtitle}
                description={data.kpis.weeklyReportCompliance?.description}
                format="percentage"
                icon={<TimeIcon />}
                variant={
                  data.kpis.weeklyReportCompliance?.value >= 80
                    ? "success"
                    : "warning"
                }
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <MetricCard
                title="Overall Compliance"
                value={data.kpis.overallCompliance?.value}
                change={data.kpis.overallCompliance?.change}
                trend={data.kpis.overallCompliance?.trend}
                subtitle={data.kpis.overallCompliance?.subtitle}
                description={data.kpis.overallCompliance?.description}
                format="percentage"
                icon={<GrowthIcon />}
                variant={
                  data.kpis.overallCompliance?.value >= 80
                    ? "success"
                    : "warning"
                }
                loading={loading}
              />
            </Grid>
          </Grid>

          {/* Main Content Grid */}
          <Grid container spacing={3}>
            {/* Client Activity Chart */}
            <Grid item xs={12} lg={8}>
              <ChartContainer
                title="Weekly Workout Completion Rates"
                subtitle="Percentage of assigned workouts completed by day"
                description="Shows the percentage of clients who successfully completed their assigned protocol workouts on each day of the week. Clients must complete at least 80% of their assigned exercises to be considered compliant. Rest days are excluded from calculations. Uses real protocol validation against expected exercise lists for accurate compliance tracking."
                loading={loading}
                error={error}
                height={400}
                actions={
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      startIcon={<SettingsIcon />}
                      onClick={() => setShowSettings(true)}
                      size="small"
                      variant="outlined"
                    >
                      Settings
                    </Button>
                    <Button
                      startIcon={<RefreshIcon />}
                      onClick={handleRefresh}
                      size="small"
                      disabled={loading || refreshing}
                    >
                      {refreshing ? "Refreshing..." : "Refresh"}
                    </Button>
                  </Box>
                }
              >
                <SimpleBarChart data={data.activityData} height={300} />
              </ChartContainer>
            </Grid>

            {/* Quick Actions */}
            <Grid item xs={12} lg={4}>
              <ChartContainer
                title="Quick Actions"
                subtitle="Common tasks and reminders"
                loading={loading}
                height={400}
              >
                <List sx={{ p: 0, pb: 2 }}>
                  {data.quickActions.map((action) => {
                    const IconComponent =
                      typeof action.icon === "string"
                        ? getIconComponent(action.icon)
                        : action.icon;

                    const handleActionClick = () => {
                      if (action.action === "navigate" && action.target) {
                        navigate(`/home${action.target}`);
                      }
                    };

                    return (
                      <ListItem
                        key={action.id}
                        onClick={handleActionClick}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          mb: 1,
                          "&:hover": {
                            backgroundColor: "action.hover",
                            cursor: "pointer",
                          },
                        }}
                      >
                        <ListItemIcon>
                          <IconComponent color={action.color} />
                        </ListItemIcon>
                        <ListItemText
                          primary={action.title}
                          secondary={action.description}
                          primaryTypographyProps={{
                            fontWeight: 500,
                            fontSize: "0.9rem",
                          }}
                          secondaryTypographyProps={{ fontSize: "0.8rem" }}
                        />
                      </ListItem>
                    );
                  })}
                </List>
              </ChartContainer>
            </Grid>

            {/* Message Alerts */}
            <Grid item xs={12} lg={6}>
              <ChartContainer
                title="Recent Alerts"
                subtitle="Important updates and notifications"
                loading={loading}
                height={600}
              >
                <List
                  sx={{
                    p: 0,
                    pb: 2,
                    maxHeight: "calc(100% - 40px)",
                    overflowY: "auto",
                  }}
                >
                  {data.alerts.map((alert) => (
                    <ListItem
                      key={alert.id}
                      sx={{
                        border: 1,
                        borderColor: "divider",
                        borderRadius: 1,
                        mb: 1.5,
                        flexDirection: "column",
                        alignItems: "stretch",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          width: "100%",
                          mb: 1,
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                          {getAlertIcon(alert.type)}
                        </ListItemIcon>
                        <Box sx={{ flex: 1 }}>
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, mb: 0.5 }}
                          >
                            {alert.title}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mb: 1 }}
                          >
                            {alert.description}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Chip
                              label={alert.action}
                              size="small"
                              color={getAlertColor(alert.type)}
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 24 }}
                            />
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {alert.time}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </ChartContainer>
            </Grid>

            {/* AI Insights */}
            <Grid item xs={12} lg={6}>
              <ChartContainer
                title="AI Insights"
                subtitle="Smart recommendations and patterns"
                loading={canUseAiInsights && aiInsightsLoading}
                height={600}
                actions={<AIIcon sx={{ color: theme.palette.primary.main }} />}
              >
                {!canUseAiInsights ? (
                  <Box sx={{ p: 2 }}>
                    <UpgradePrompt
                      organizationData={organizationData}
                      minTier="pro"
                      title="Upgrade for AI insights"
                      description="Dashboard AI insights are available on Pro."
                      featureName="ai_insights"
                    />
                  </Box>
                ) : (
                <List
                  sx={{
                    p: 0,
                    pb: 2,
                    maxHeight: "calc(600px - 120px)", // Account for container header
                    overflowY: "auto",
                    "&::-webkit-scrollbar": {
                      width: 6,
                    },
                    "&::-webkit-scrollbar-track": {
                      backgroundColor: "rgba(0,0,0,0.1)",
                      borderRadius: 3,
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: "rgba(0,0,0,0.3)",
                      borderRadius: 3,
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.5)",
                      },
                    },
                  }}
                >
                  {aiInsightsLoading ? (
                    <ListItem sx={{ justifyContent: "center", py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        AI is analyzing your metrics...
                      </Typography>
                    </ListItem>
                  ) : data.aiInsights.length === 0 ? (
                    <ListItem sx={{ justifyContent: "center", py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No AI insights available yet
                      </Typography>
                    </ListItem>
                  ) : (
                    data.aiInsights.map((insight) => (
                      <ListItem
                        key={insight.id}
                        sx={{
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                          mb: 1.5,
                          flexDirection: "column",
                          alignItems: "stretch",
                          backgroundColor: "background.paper",
                        }}
                      >
                        <Box sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "flex-start",
                              mb: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500, color: "primary.main" }}
                            >
                              {insight.title}
                            </Typography>
                            <Chip
                              label={insight.confidence}
                              size="small"
                              color={
                                insight.confidence === "High"
                                  ? "success"
                                  : "default"
                              }
                              sx={{ fontSize: "0.7rem", height: 20 }}
                            />
                          </Box>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ fontSize: "0.85rem", lineHeight: 1.4 }}
                          >
                            {insight.insight}
                          </Typography>
                          <Box sx={{ mt: 1 }}>
                            <Chip
                              label={insight.type}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: "0.7rem", height: 22 }}
                            />
                          </Box>
                        </Box>
                      </ListItem>
                    ))
                  )}
                </List>
                )}
              </ChartContainer>
            </Grid>

            {/* Growth Trend */}
            <Grid item xs={12}>
              <ChartContainer
                title="Overall Compliance Trends"
                subtitle="Combined compliance scores (workouts + reports + photos) over time"
                loading={loading}
                error={error}
                height={300}
              >
                <SimpleLineChart
                  data={data.growthData}
                  height={180}
                  color={theme.palette.primary.main}
                />
              </ChartContainer>
            </Grid>
          </Grid>
        </>
      )}

      {/* Settings Dialog */}
      <Dialog
        open={showSettings}
        onClose={() => setShowSettings(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Dashboard Configuration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Adjust thresholds and calculation methods to customize how
            compliance metrics are calculated.
          </Typography>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Report Compliance Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Report Quality Threshold (%)"
                    type="number"
                    value={config.reportQualityThreshold}
                    onChange={(e) =>
                      handleConfigChange(
                        "reportQualityThreshold",
                        parseInt(e.target.value),
                      )
                    }
                    helperText="Minimum % of weekly questions that must be answered"
                    fullWidth
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Client Compliance Threshold (%)"
                    type="number"
                    value={config.clientComplianceThreshold}
                    onChange={(e) =>
                      handleConfigChange(
                        "clientComplianceThreshold",
                        parseInt(e.target.value),
                      )
                    }
                    helperText="Minimum % for a client to be considered compliant"
                    fullWidth
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.useAverageCompliance}
                        onChange={(e) =>
                          handleConfigChange(
                            "useAverageCompliance",
                            e.target.checked,
                          )
                        }
                      />
                    }
                    label="Use Average Compliance (vs Minimum)"
                    helperText="Calculate compliance as average of submission & quality rates instead of minimum"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Workout & Photo Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Workout Compliance Threshold (%)"
                    type="number"
                    value={config.workoutComplianceThreshold}
                    onChange={(e) =>
                      handleConfigChange(
                        "workoutComplianceThreshold",
                        parseInt(e.target.value),
                      )
                    }
                    helperText="Minimum % of workouts completed for compliance"
                    fullWidth
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Photo Compliance Threshold (%)"
                    type="number"
                    value={config.photoComplianceThreshold}
                    onChange={(e) =>
                      handleConfigChange(
                        "photoComplianceThreshold",
                        parseInt(e.target.value),
                      )
                    }
                    helperText="Minimum % of photo submissions for compliance"
                    fullWidth
                    inputProps={{ min: 0, max: 100 }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Debug Settings</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.enableDetailedLogging}
                    onChange={(e) =>
                      handleConfigChange(
                        "enableDetailedLogging",
                        e.target.checked,
                      )
                    }
                  />
                }
                label="Enable Detailed Logging"
                helperText="Show detailed debug information in browser console"
              />
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSettings(false)}>Cancel</Button>
          <Button onClick={handleApplyConfig} variant="contained">
            Apply Settings
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OverviewModule;
